import { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { GuideAvailability, GuideDaySchedule } from '@/types';
import { GuideAvailabilitySource, GuideAvailabilityStatus } from '@/types';
import { formatClockTime } from '@/features/booking/utils/timeSlots';
import { useDeleteAvailability } from '@/features/guide/hooks/useGuideAvailability';

interface BlockedDatesListProps {
  entries: GuideAvailability[];
  /** Days with booked time (from the guide schedule). */
  bookedDays: GuideDaySchedule[];
  isLoading: boolean;
  error: Error | null;
}

function formatDisplayDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function BlockedDatesList({ entries, bookedDays, isLoading, error }: BlockedDatesListProps) {
  const deleteAvailability = useDeleteAvailability();

  const leaveEntries = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return entries.filter(
      (entry) =>
        entry.date >= today &&
        entry.status === GuideAvailabilityStatus.VACATION &&
        entry.source !== GuideAvailabilitySource.BOOKING
    );
  }, [entries]);

  const handleDelete = async (entry: GuideAvailability) => {
    const confirmed = window.confirm(`Remove your leave block for ${formatDisplayDate(entry.date)}?`);
    if (!confirmed) return;
    await deleteAvailability.mutateAsync(entry.id);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.75 }}>
        Upcoming unavailable dates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Booked hours are locked by the system. Leave dates can be removed anytime before a booking happens.
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !isLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load blocked dates. Please try again.
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Booked time
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Only the booked hours (plus a 30-minute buffer) are blocked; travelers can still book the rest of the day.
              </Typography>
              {bookedDays.length === 0 ? (
                <Alert severity="info">No upcoming bookings in the next two months.</Alert>
              ) : (
                <List disablePadding>
                  {bookedDays.map((day, index) => (
                    <Box key={day.date}>
                      <ListItem
                        disableGutters
                        secondaryAction={
                          <Tooltip title="Blocked automatically by a booking">
                            <LockOutlinedIcon color="action" fontSize="small" />
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={formatDisplayDate(day.date)}
                          secondary={day.busy
                            .map((range) => `${formatClockTime(range.from)} – ${formatClockTime(range.to)}`)
                            .join(', ')}
                        />
                        <Chip
                          label={day.status === 'full' ? 'Fully booked' : 'Partly booked'}
                          size="small"
                          sx={{ ml: 2 }}
                          color="warning"
                        />
                      </ListItem>
                      {index < bookedDays.length - 1 && <Divider component="li" />}
                    </Box>
                  ))}
                </List>
              )}
            </Paper>

            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Leave dates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Remove any leave date here when you become available again.
              </Typography>
              {leaveEntries.length === 0 ? (
                <Alert severity="info">No leave dates set yet. Use the add button to block dates.</Alert>
              ) : (
                <List disablePadding>
                  {leaveEntries.map((entry, index) => (
                    <Box key={entry.id}>
                      <ListItem
                        disableGutters
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label={`Remove leave date ${entry.date}`}
                            onClick={() => handleDelete(entry)}
                            disabled={deleteAvailability.isPending}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={formatDisplayDate(entry.date)}
                          secondary={entry.notes || 'Marked as personal leave'}
                        />
                        <Chip label="On leave" size="small" color="error" sx={{ ml: 2 }} />
                      </ListItem>
                      {index < leaveEntries.length - 1 && <Divider component="li" />}
                    </Box>
                  ))}
                </List>
              )}
            </Paper>
          </Box>

          {deleteAvailability.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(deleteAvailability.error as Error).message || 'Failed to remove leave date.'}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
